// Custom Tiptap extension for variable pills
// Renders {{variable_key}} as interactive pills in the editor
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { getVariableLabelFromKey } from '../../../../../../../../constants/templateVariables';
import VariablePillNodeView from './VariablePillNodeView';

export interface VariablePillOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variablePill: {
      /**
       * Insert a variable pill
       */
      insertVariablePill: (variableKey: string) => ReturnType;
    };
  }
}

export const VariablePill = Node.create<VariablePillOptions>({
  name: 'variablePill',

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      variableKey: {
        default: null,
        parseHTML: element => element.getAttribute('data-variable-key'),
        renderHTML: attributes => {
          if (!attributes.variableKey) {
            return {};
          }
          return {
            'data-variable-key': attributes.variableKey,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-variable-pill]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const variableKey = node.attrs.variableKey;
    const label = getVariableLabelFromKey(variableKey);

    return [
      'span',
      mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes,
        {
          'data-variable-pill': '',
          'data-variable-key': variableKey,
          'class': 'variable-pill',
          'contenteditable': 'false',
        }
      ),
      [
        'span',
        { class: 'pill-label' },
        label
      ],
      [
        'button',
        {
          class: 'pill-delete',
          contenteditable: 'false',
          type: 'button',
        },
        '×'
      ],
    ];
  },

  renderText({ node }) {
    // Output as {{variable_key}} in plain text/markdown
    return `{{${node.attrs.variableKey}}}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariablePillNodeView);
  },

  addCommands() {
    return {
      insertVariablePill:
        (variableKey: string) =>
        ({ chain }) => {
          console.log('📌 INSERTING PILL:', variableKey);

          // Insert the pill and focus
          return chain()
            .insertContent({
              type: this.name,
              attrs: { variableKey },
            })
            .focus()
            .run();
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Delete pill with Backspace
      Backspace: () => this.editor.commands.deleteSelection(),
      // Delete pill with Delete
      Delete: () => this.editor.commands.deleteSelection(),
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('variablePillClick'),
        props: {
          handleDOMEvents: {
            // Handle clicks on the delete button
            click: (view, event) => {
              const target = event.target as HTMLElement;

              // Check if clicked on delete button
              if (target.classList.contains('pill-delete')) {
                event.preventDefault();
                event.stopPropagation();

                const pill = target.closest('.variable-pill');
                if (pill) {
                  // Find the node position and delete it
                  const pos = view.posAtDOM(pill, 0);
                  const tr = view.state.tr.delete(pos, pos + 1);
                  view.dispatch(tr);
                  return true;
                }
              }

              return false;
            },
          },
        },
      }),
    ];
  },
});
